import { SaleOrderStatus } from 'src/app/shared/enums/sale-order-status.enum';

export enum ConsolidatedShipmentStatusGroup {
    Created = 'created',
    InSortingWarehouse = 'in_sorting_warehouse',
    OutForDelivery = 'out_for_delivery',
    ReadyForPickup = 'ready_for_pickup',
    PickedUpByCustomer = 'picked_up_by_customer',
    Expired = 'expired',
    Return = 'return',
    Cancel = 'cancel',
    EmptyLockerFound = 'empty_locker_found',
}

export const CONSOLIDATED_STATUS_GROUP_LABELS: Record<ConsolidatedShipmentStatusGroup, string> = {
    [ConsolidatedShipmentStatusGroup.Created]: 'Created',
    [ConsolidatedShipmentStatusGroup.InSortingWarehouse]: 'In sorting warehouse',
    [ConsolidatedShipmentStatusGroup.OutForDelivery]: 'Out for delivery',
    [ConsolidatedShipmentStatusGroup.ReadyForPickup]: 'Ready for pickup',
    [ConsolidatedShipmentStatusGroup.PickedUpByCustomer]: 'Picked up by customer',
    [ConsolidatedShipmentStatusGroup.Expired]: 'Expired',
    [ConsolidatedShipmentStatusGroup.Return]: 'Return',
    [ConsolidatedShipmentStatusGroup.Cancel]: 'Cancel',
    [ConsolidatedShipmentStatusGroup.EmptyLockerFound]: 'Empty locker found',
};

/** Display order for grouped admin summary items. */
export const CONSOLIDATED_STATUS_GROUP_ORDER: ConsolidatedShipmentStatusGroup[] = [
    ConsolidatedShipmentStatusGroup.Created,
    ConsolidatedShipmentStatusGroup.InSortingWarehouse,
    ConsolidatedShipmentStatusGroup.OutForDelivery,
    ConsolidatedShipmentStatusGroup.ReadyForPickup,
    ConsolidatedShipmentStatusGroup.PickedUpByCustomer,
    ConsolidatedShipmentStatusGroup.Expired,
    ConsolidatedShipmentStatusGroup.Return,
    ConsolidatedShipmentStatusGroup.Cancel,
    ConsolidatedShipmentStatusGroup.EmptyLockerFound,
];

const STATUS_TO_GROUP = new Map<number, ConsolidatedShipmentStatusGroup>([
    // Created — dropped_off webhook, created, first_leg_shipment_created, shipped
    [SaleOrderStatus.DROPPED_OFF, ConsolidatedShipmentStatusGroup.Created],
    [SaleOrderStatus.VINTEDGO_CREATED, ConsolidatedShipmentStatusGroup.Created],
    [SaleOrderStatus.VINTEDGO_FIRST_LEG_SHIPMENT_CREATED, ConsolidatedShipmentStatusGroup.Created],

    // In sorting warehouse
    [SaleOrderStatus.IN_TRANSIT, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_SORTING_CENTER, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_SORTED, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_LOADED_TO_TRUCK, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_PARTNER_TIME_SLOT_SELECTED, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_ON_THE_ROAD, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_DELIVERY, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.INTRANSIT_001, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_LEFT_OUR_NETWORK, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_RELABELLED, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_EXTERNAL_REDIRECTED, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.EXCEPTION_013, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    [SaleOrderStatus.VINTEDGO_DISPOSED, ConsolidatedShipmentStatusGroup.InSortingWarehouse],
    // external_cancelled (and cancelled) map to CANCELED
    [SaleOrderStatus.CANCELED, ConsolidatedShipmentStatusGroup.InSortingWarehouse],

    // Out for delivery — out_for_delivery, partner_out_for_delivery
    [SaleOrderStatus.OUTFORDELIVERY_001, ConsolidatedShipmentStatusGroup.OutForDelivery],
    [SaleOrderStatus.VINTEDGO_PARTNER_OUT_FOR_DELIVERY, ConsolidatedShipmentStatusGroup.OutForDelivery],

    // Ready for pickup — notifications, ready_for_pickup, in_transfer_point
    [SaleOrderStatus.AVAILABLEFORPICKUP_001, ConsolidatedShipmentStatusGroup.ReadyForPickup],
    [SaleOrderStatus.VINTEDGO_IN_TRANSFER_POINT, ConsolidatedShipmentStatusGroup.ReadyForPickup],
    [SaleOrderStatus.VINTEDGO_SMS_NOTIFICATION_SENT, ConsolidatedShipmentStatusGroup.ReadyForPickup],
    [SaleOrderStatus.VINTEDGO_EMAIL_NOTIFICATION_SENT, ConsolidatedShipmentStatusGroup.ReadyForPickup],
    [SaleOrderStatus.VINTEDGO_REMINDER_SMS_NOTIFICATION_SENT, ConsolidatedShipmentStatusGroup.ReadyForPickup],
    [SaleOrderStatus.VINTEDGO_REMINDER_EMAIL_NOTIFICATION_SENT, ConsolidatedShipmentStatusGroup.ReadyForPickup],

    // Picked up by customer
    [SaleOrderStatus.DELIVERED_001, ConsolidatedShipmentStatusGroup.PickedUpByCustomer],
    [SaleOrderStatus.DELIVERED_002, ConsolidatedShipmentStatusGroup.PickedUpByCustomer],

    // Expired — shipment.expired webhook, EXPIRED_001 (incl. pickup_expired_return)
    [SaleOrderStatus.EXPIRED_001, ConsolidatedShipmentStatusGroup.Expired],

    // Return
    [SaleOrderStatus.VINTEDGO_RETURN, ConsolidatedShipmentStatusGroup.Return],
    [SaleOrderStatus.VINTEDGO_OVERSIZED_RETURN, ConsolidatedShipmentStatusGroup.Return],
    [SaleOrderStatus.EXCEPTION_011, ConsolidatedShipmentStatusGroup.Return],
    [SaleOrderStatus.VINTEDGO_PARCEL_COLLECTED_BY_SENDER, ConsolidatedShipmentStatusGroup.Return],
    [SaleOrderStatus.EXCEPTION_010, ConsolidatedShipmentStatusGroup.Return],

    // Cancel — drop-off cancels only (external_cancelled/cancelled → In sorting warehouse)
    [SaleOrderStatus.VINTEDGO_CANCEL_DROP_OFF, ConsolidatedShipmentStatusGroup.Cancel],
    [SaleOrderStatus.VINTEDGO_DROP_OFF_CANCELLED_BY_CS, ConsolidatedShipmentStatusGroup.Cancel],

    // Empty locker found
    [SaleOrderStatus.VINTEDGO_EMPTY_LOCKER_FOUND, ConsolidatedShipmentStatusGroup.EmptyLockerFound],
]);

export interface IStatusCountItem {
    /** Set for grouped admin labels. */
    label?: string;
    /** Set for unmapped statuses — label comes from deliveryStatusLabel pipe. */
    status?: number;
    count: number;
}

const UNUSUAL_STATUS_PATTERN = /return|refund|cancel/i;

export function isUnusualStatusLabel(label: string | null | undefined): boolean {
    return UNUSUAL_STATUS_PATTERN.test(label ?? '');
}

export function getConsolidatedStatusGroup(status: number): ConsolidatedShipmentStatusGroup | null {
    return STATUS_TO_GROUP.get(status) ?? null;
}

/** All statuses in the Ready for pickup group participate in the 7-day pickup countdown. */
export function isReadyForPickupGroupStatus(status: number | null | undefined): boolean {
    if (status === undefined || status === null) {
        return false;
    }

    return getConsolidatedStatusGroup(Number(status)) === ConsolidatedShipmentStatusGroup.ReadyForPickup;
}

/** In sorting warehouse group — used for Days in Warehouse / SO in warehouse counts. */
export function isInSortingWarehouseGroupStatus(status: number | null | undefined): boolean {
    if (status === undefined || status === null) {
        return false;
    }

    return getConsolidatedStatusGroup(Number(status)) === ConsolidatedShipmentStatusGroup.InSortingWarehouse;
}

export function getConsolidatedStatusLabel(status: number | null | undefined): string | null {
    if (status === undefined || status === null) {
        return null;
    }

    const group = getConsolidatedStatusGroup(Number(status));
    return group ? CONSOLIDATED_STATUS_GROUP_LABELS[group] : null;
}

export interface IConsolidatedStatusGroupOption {
    id: ConsolidatedShipmentStatusGroup;
    label: string;
}

export function getConsolidatedStatusGroupFilterOptions(): IConsolidatedStatusGroupOption[] {
    return CONSOLIDATED_STATUS_GROUP_ORDER.map((group) => ({
        id: group,
        label: CONSOLIDATED_STATUS_GROUP_LABELS[group],
    }));
}

export function filterConsolidatedStatusGroupOptions(
    options: IConsolidatedStatusGroupOption[],
    searchText: string | null | undefined,
): IConsolidatedStatusGroupOption[] {
    const query = searchText?.trim().toLowerCase();
    if (!query) {
        return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(query));
}

export function getStatusIdsForGroups(groups: ConsolidatedShipmentStatusGroup[]): Set<number> {
    const ids = new Set<number>();

    for (const [statusId, group] of STATUS_TO_GROUP.entries()) {
        if (groups.includes(group)) {
            ids.add(statusId);
        }
    }

    return ids;
}

export interface IStatusCountRow {
    status?: number | string | null;
    empactStatusId?: number | string | null;
    count?: number | string | null;
    ordersCount?: number | string | null;
}

export function buildGroupedStatusCountsFromRows(
    rows: IStatusCountRow[] | null | undefined,
): IStatusCountItem[] {
    const countByGroup = new Map<ConsolidatedShipmentStatusGroup, number>();
    const countByUnmappedStatus = new Map<number, number>();

    for (const row of rows ?? []) {
        const rawStatus = row?.status ?? row?.empactStatusId;
        if (rawStatus === undefined || rawStatus === null || rawStatus === '') {
            continue;
        }

        const status = Number(rawStatus);
        const increment = Number(row?.ordersCount ?? row?.count ?? 0);
        if (Number.isNaN(status) || !Number.isFinite(increment) || increment <= 0) {
            continue;
        }

        const group = getConsolidatedStatusGroup(status);
        if (group) {
            countByGroup.set(group, (countByGroup.get(group) ?? 0) + increment);
        } else {
            countByUnmappedStatus.set(status, (countByUnmappedStatus.get(status) ?? 0) + increment);
        }
    }

    const groupedItems: IStatusCountItem[] = CONSOLIDATED_STATUS_GROUP_ORDER
        .map((group) => ({
            label: CONSOLIDATED_STATUS_GROUP_LABELS[group],
            count: countByGroup.get(group) ?? 0,
        }))
        .filter((item) => item.count > 0);

    const unmappedItems: IStatusCountItem[] = Array.from(countByUnmappedStatus.entries())
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count || a.status - b.status);

    return [...groupedItems, ...unmappedItems];
}

export function buildGroupedStatusCounts(
    orders: Array<{ latestOperationalStatusId?: number | string | null }> | null | undefined,
): IStatusCountItem[] {
    return buildGroupedStatusCountsFromRows(
        (orders ?? []).map((order) => ({
            status: order?.latestOperationalStatusId,
            count: 1,
        })),
    );
}
